import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  Share2, Copy, FileDown, Loader2, RefreshCw, Save, Plus, Trash2,
  Building2, Activity, Webhook, KeyRound, Eye, EyeOff, ChevronDown, ChevronRight
} from 'lucide-react';

const fmtEur = (c) => (Number(c || 0) / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDt = (d) => d ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

const FIELD_TYPES = ['text', 'textarea', 'html', 'number', 'bool', 'select', 'multiselect', 'list', 'gallery'];

export default function MypasspartuConsole() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [cfg, setCfg] = useState({ webhook_url: '', webhook_secret: '', spoki_webhook_url: '', spoki_secret: '', whatsapp_cost_cents: 10 });
  const [savingCfg, setSavingCfg] = useState(false);
  const [schema, setSchema] = useState(null);
  const [savingSchema, setSavingSchema] = useState(false);
  const [extProps, setExtProps] = useState([]);
  const [extSearch, setExtSearch] = useState('');
  const [expandedProp, setExpandedProp] = useState(null);
  const [whLog, setWhLog] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const loadOverview = async () => {
    try {
      const res = await api.get('/admin/mypasspartu/overview');
      setOverview(res.data);
      setCfg(c => ({ ...c, webhook_url: res.data.config.webhook_url, spoki_webhook_url: res.data.config.spoki_webhook_url, whatsapp_cost_cents: res.data.config.whatsapp_cost_cents }));
    } catch { toast.error('Errore caricamento panoramica'); }
    setLoading(false);
  };

  useEffect(() => { loadOverview(); }, []);
  useEffect(() => {
    if (tab === 'schema' && !schema) api.get('/admin/mypasspartu/schema').then(r => setSchema(r.data)).catch(() => toast.error('Errore schema'));
    if (tab === 'properties') loadExtProps();
    if (tab === 'log') loadLog();
  }, [tab]);

  const loadExtProps = async (q = extSearch) => {
    try { const r = await api.get(`/admin/mypasspartu/external-properties${q ? `?search=${encodeURIComponent(q)}` : ''}`); setExtProps(r.data.properties || []); }
    catch { toast.error('Errore immobili di terzi'); }
  };
  const loadLog = async () => {
    try { const r = await api.get('/admin/mypasspartu/webhook-log'); setWhLog(r.data.log || []); }
    catch { toast.error('Errore log webhook'); }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const body = { webhook_url: cfg.webhook_url, spoki_webhook_url: cfg.spoki_webhook_url, whatsapp_cost_cents: cfg.whatsapp_cost_cents };
      if (cfg.webhook_secret) body.webhook_secret = cfg.webhook_secret;
      if (cfg.spoki_secret) body.spoki_secret = cfg.spoki_secret;
      await api.put('/admin/mypasspartu/config', body);
      toast.success('Configurazione salvata');
      setCfg(c => ({ ...c, webhook_secret: '', spoki_secret: '' }));
      loadOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    setSavingCfg(false);
  };

  const saveSchema = async () => {
    setSavingSchema(true);
    try {
      const res = await api.put('/admin/mypasspartu/schema', { fields: schema.fields });
      toast.success(res.data.message);
      const r = await api.get('/admin/mypasspartu/schema');
      setSchema(r.data);
      loadOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio schema'); }
    setSavingSchema(false);
  };

  const updateField = (idx, patch) => setSchema(s => ({ ...s, fields: s.fields.map((f, i) => i === idx ? { ...f, ...patch } : f) }));
  const toggleAudience = (idx, aud) => setSchema(s => ({
    ...s,
    fields: s.fields.map((f, i) => {
      if (i !== idx) return f;
      const has = (f.audiences || []).includes(aud);
      return { ...f, audiences: has ? (f.audiences || []).filter(a => a !== aud) : [...(f.audiences || []), aud] };
    })
  }));
  const addField = () => setSchema(s => ({
    ...s,
    fields: [...s.fields, { key: `campo_${Date.now().toString(36)}`, label: 'Nuovo campo', type: 'text', section: 'Altro', order: (Math.max(0, ...s.fields.map(f => f.order || 0)) + 1), audiences: ['terzi'], required_terzi: false }]
  }));
  const removeField = (idx) => setSchema(s => ({ ...s, fields: s.fields.filter((_, i) => i !== idx) }));

  const copyKey = () => { navigator.clipboard.writeText(overview?.service_key || ''); toast.success('Chiave copiata'); };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/admin/report/mypasspartu.pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('Errore generazione report'); }
    setDownloading(false);
  };

  const retryWebhook = async (id) => {
    try {
      const r = await api.post(`/admin/mypasspartu/webhook-retry/${id}`);
      r.data.ok ? toast.success('Webhook consegnato') : toast.error(`Ancora fallito: ${r.data.error || r.data.status}`);
      loadLog();
    } catch { toast.error('Errore retry'); }
  };

  const tabs = [
    { id: 'overview', label: 'Panoramica', icon: Activity },
    { id: 'schema', label: 'Schema Scheda', icon: KeyRound },
    { id: 'properties', label: 'Immobili di Terzi', icon: Building2 },
    { id: 'log', label: 'Log Webhook', icon: Webhook },
  ];

  const st = overview?.stats;

  return (
    <Layout>
      <div className="space-y-5" data-testid="mypasspartu-console">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Share2 className="h-6 w-6 text-violet-600" /> Collegamento Mypasspartu
            </h1>
            <p className="text-slate-500">Motore API per la piattaforma Mypasspartu: schema, immobili di terzi, consumi e webhook</p>
          </div>
          <Button onClick={downloadReport} disabled={downloading} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="mpp-report-btn">
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
            Report Collegamento PDF
          </Button>
        </div>

        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 flex-wrap">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`mpp-tab-${t.id}`}
              className={`flex-1 min-w-[140px] px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Card className="p-10 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></Card>
        ) : tab === 'overview' && overview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card className="p-3"><p className="text-[11px] text-slate-400 uppercase">Tenant visti</p><p className="text-xl font-bold" data-testid="mpp-stat-tenants">{st.tenants_seen}</p></Card>
              <Card className="p-3"><p className="text-[11px] text-slate-400 uppercase">Immobili terzi attivi</p><p className="text-xl font-bold">{st.external_properties_active}</p></Card>
              <Card className="p-3"><p className="text-[11px] text-slate-400 uppercase">Eventi mercato</p><p className="text-xl font-bold">{st.market_events}</p></Card>
              <Card className="p-3"><p className="text-[11px] text-slate-400 uppercase">Schema versione</p><p className="text-xl font-bold text-violet-600" data-testid="mpp-schema-version">v{overview.schema_version}</p></Card>
              <Card className={`p-3 ${st.webhook_failed > 0 ? 'border-red-300 bg-red-50/60' : ''}`}><p className="text-[11px] text-slate-400 uppercase">Webhook falliti</p><p className={`text-xl font-bold ${st.webhook_failed > 0 ? 'text-red-600' : ''}`}>{st.webhook_failed}</p></Card>
            </div>

            <Card className="p-4 space-y-2">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2"><KeyRound className="h-4 w-4 text-violet-500" /> Chiave di servizio (da incollare nel progetto Mypasspartu come HW_MOTOR_SERVICE_KEY)</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-100 rounded px-3 py-2 text-xs font-mono break-all" data-testid="mpp-service-key">
                  {showKey ? overview.service_key : '•'.repeat(24) + overview.service_key.slice(-6)}
                </code>
                <Button variant="outline" size="sm" onClick={() => setShowKey(v => !v)}>{showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                <Button variant="outline" size="sm" onClick={copyKey} data-testid="mpp-copy-key"><Copy className="h-4 w-4" /></Button>
              </div>
              <p className="text-xs text-slate-400">Base URL motore: <code className="bg-slate-100 px-1 rounded">https://hwsmart.it{overview.api_base}</code> · Header: <code className="bg-slate-100 px-1 rounded">X-Service-Key</code> + <code className="bg-slate-100 px-1 rounded">X-Tenant-Id</code></p>
            </Card>

            <Card className="p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-700">Configurazione</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500">URL Webhook Mypasspartu (riceve schema.updated)</label>
                  <Input value={cfg.webhook_url} onChange={e => setCfg(c => ({ ...c, webhook_url: e.target.value }))} placeholder="https://mypasspartu.it/api/integrations/hw-motor/webhook" data-testid="mpp-cfg-webhook-url" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Webhook secret {overview.config.webhook_secret_set && <span className="text-green-600">(impostato)</span>}</label>
                  <Input type="password" value={cfg.webhook_secret} onChange={e => setCfg(c => ({ ...c, webhook_secret: e.target.value }))} placeholder="Lascia vuoto per non cambiare" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Spoki webhook generico (messaggi terzi)</label>
                  <Input value={cfg.spoki_webhook_url} onChange={e => setCfg(c => ({ ...c, spoki_webhook_url: e.target.value }))} placeholder="https://app.spoki.it/wh/ap/..." />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Spoki secret {overview.config.spoki_secret_set && <span className="text-green-600">(impostato)</span>}</label>
                  <Input type="password" value={cfg.spoki_secret} onChange={e => setCfg(c => ({ ...c, spoki_secret: e.target.value }))} placeholder="Lascia vuoto per non cambiare" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Costo WhatsApp per messaggio (centesimi)</label>
                  <Input type="number" value={cfg.whatsapp_cost_cents} onChange={e => setCfg(c => ({ ...c, whatsapp_cost_cents: e.target.value }))} data-testid="mpp-cfg-cost" />
                </div>
              </div>
              <Button onClick={saveConfig} disabled={savingCfg} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="mpp-save-config">
                {savingCfg ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salva configurazione
              </Button>
            </Card>

            {st.usage_month?.length > 0 && (
              <Card className="p-4">
                <p className="text-sm font-semibold text-slate-700 mb-2">Consumi del mese</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {st.usage_month.map(u => (
                    <div key={u.service} className="bg-slate-50 rounded p-2 text-xs">
                      <p className="font-mono text-slate-600">{u.service}</p>
                      <p className="font-bold">{u.count} chiamate · {fmtEur(u.cost_cents)}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {tab === 'schema' && (
          !schema ? <Card className="p-10 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></Card> : (
            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Schema scheda immobile — versione v{schema.version}</p>
                  <p className="text-xs text-slate-400">Spunta dove ogni campo è visibile. Al salvataggio la versione aumenta e Mypasspartu si aggiorna da sola via webhook.</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={addField} data-testid="mpp-add-field"><Plus className="h-4 w-4 mr-1" /> Aggiungi campo</Button>
                  <Button size="sm" onClick={saveSchema} disabled={savingSchema} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="mpp-save-schema">
                    {savingSchema ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salva (v{schema.version + 1})
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="mpp-schema-table">
                  <thead className="bg-slate-50 border-b">
                    <tr className="text-xs text-slate-500 uppercase">
                      <th className="px-2 py-2 text-left">Etichetta</th>
                      <th className="px-2 py-2 text-left">Chiave</th>
                      <th className="px-2 py-2 text-left">Tipo</th>
                      <th className="px-2 py-2 text-left">Sezione</th>
                      <th className="px-2 py-2 text-center">HW</th>
                      <th className="px-2 py-2 text-center">Terzi</th>
                      <th className="px-2 py-2 text-center">Obblig. Terzi</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {schema.fields.map((f, idx) => (
                      <tr key={idx} className="hover:bg-violet-50/30" data-testid={`mpp-field-row-${f.key}`}>
                        <td className="px-2 py-1.5"><Input value={f.label} onChange={e => updateField(idx, { label: e.target.value })} className="h-8 text-sm" /></td>
                        <td className="px-2 py-1.5"><Input value={f.key} onChange={e => updateField(idx, { key: e.target.value })} className="h-8 text-xs font-mono w-36" /></td>
                        <td className="px-2 py-1.5">
                          <select value={f.type} onChange={e => updateField(idx, { type: e.target.value })} className="h-8 border border-slate-300 rounded text-xs bg-white px-1">
                            {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1.5"><Input value={f.section || ''} onChange={e => updateField(idx, { section: e.target.value })} className="h-8 text-xs w-32" /></td>
                        <td className="px-2 py-1.5 text-center">
                          <input type="checkbox" checked={(f.audiences || []).includes('hw')} onChange={() => toggleAudience(idx, 'hw')} className="h-4 w-4" data-testid={`mpp-field-hw-${f.key}`} />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <input type="checkbox" checked={(f.audiences || []).includes('terzi')} onChange={() => toggleAudience(idx, 'terzi')} className="h-4 w-4" data-testid={`mpp-field-terzi-${f.key}`} />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <input type="checkbox" checked={!!f.required_terzi} onChange={e => updateField(idx, { required_terzi: e.target.checked })} className="h-4 w-4" />
                        </td>
                        <td className="px-2 py-1.5">
                          <button onClick={() => removeField(idx)} className="text-red-400 hover:text-red-600" title="Rimuovi campo"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )
        )}

        {tab === 'properties' && (
          <Card className="p-4 space-y-3">
            <div className="flex gap-2">
              <Input value={extSearch} onChange={e => setExtSearch(e.target.value)} placeholder="Cerca per tenant, nome, città..." className="max-w-sm" data-testid="mpp-ext-search" />
              <Button variant="outline" size="sm" onClick={() => loadExtProps()}><RefreshCw className="h-4 w-4" /></Button>
            </div>
            {extProps.length === 0 ? (
              <p className="text-sm text-slate-400 p-6 text-center">Nessun immobile di terzi ricevuto ancora. Arriveranno qui automaticamente quando gli utenti Mypasspartu creeranno le loro schede.</p>
            ) : (
              <div className="divide-y divide-slate-100 border rounded-lg" data-testid="mpp-ext-list">
                {extProps.map(p => (
                  <div key={p.id}>
                    <div className="p-3 flex items-center gap-3 cursor-pointer hover:bg-violet-50/40" onClick={() => setExpandedProp(expandedProp === p.id ? null : p.id)}>
                      {expandedProp === p.id ? <ChevronDown className="h-4 w-4 text-violet-500" /> : <ChevronRight className="h-4 w-4 text-slate-300" />}
                      <Building2 className={`h-4 w-4 ${p.status === 'active' ? 'text-green-500' : 'text-slate-300'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{p.data?.name || p.external_id}</p>
                        <p className="text-xs text-slate-400">tenant: {p.tenant_id} · {p.data?.city || '—'} · agg. {fmtDt(p.updated_at)}</p>
                      </div>
                      {p.enabled_on_hw && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full shrink-0">Pubblica su HW</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{p.status}</span>
                    </div>
                    {expandedProp === p.id && (
                      <div className="px-10 pb-3">
                        <pre className="bg-slate-50 rounded p-3 text-xs overflow-x-auto max-h-64">{JSON.stringify(p.data, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {tab === 'log' && (
          <Card className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Ultimi webhook inviati a Mypasspartu</p>
              <Button variant="outline" size="sm" onClick={loadLog}><RefreshCw className="h-4 w-4" /></Button>
            </div>
            {whLog.length === 0 ? (
              <p className="text-sm text-slate-400 p-6 text-center">Nessun webhook inviato ancora</p>
            ) : (
              <div className="divide-y divide-slate-100 border rounded-lg" data-testid="mpp-webhook-log">
                {whLog.map(l => (
                  <div key={l.id} className="p-3 flex items-center gap-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${l.status === 'delivered' ? 'bg-green-100 text-green-700' : l.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>{l.status}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono text-slate-700">{l.event}</p>
                      <p className="text-xs text-slate-400 truncate">{fmtDt(l.created_at)} · tentativi {l.attempts}{l.last_error ? ` · ${l.last_error}` : ''}</p>
                    </div>
                    {l.status === 'failed' && (
                      <Button variant="outline" size="sm" onClick={() => retryWebhook(l.id)} className="border-amber-300 text-amber-700" data-testid={`mpp-retry-${l.id}`}>Riprova</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
}
